import React, { useState } from 'react'
import {
  AppDialog,
  Button,
  SegmentedControl,
  TextInput,
  Textarea,
  PhoneNumberInput,
} from '../../Common'
import { useNls } from '../../../nls/nlsProvider'
import type {
  OutsideApprovalMode,
  OutsideApprovalSubjectType,
} from '../../../types/ops'
import { createOutsideApproval } from '../../../services/opsApprovalsService'
import './approvals.css'

export interface ApproverInput {
  name: string
  phone: string
}

export interface SendForApprovalDialogProps {
  open: boolean
  onDismiss: () => void
  workspaceId: string
  subjectType: OutsideApprovalSubjectType
  subjectId: string
  onSuccess?: () => void
  onSendCustom?: (input: {
    workspaceId: string
    approvers: Array<{ name: string; phone: string }>
    mode: OutsideApprovalMode
    expiresInDays: number
    message?: string
    opId: string
  }) => Promise<{
    approvalId: string
    approvalState: string
    links?: Array<{ approverId: string; phoneMasked: string; token: string }>
  }>
}

export const SendForApprovalDialog: React.FC<SendForApprovalDialogProps> = ({
  open,
  onDismiss,
  workspaceId,
  subjectType,
  subjectId,
  onSuccess,
  onSendCustom,
}) => {
  const { t } = useNls()

  const [approvers, setApprovers] = useState<ApproverInput[]>([
    { name: '', phone: '' },
  ])
  const [mode, setMode] = useState<OutsideApprovalMode>('ALL')
  const [expiresInDays, setExpiresInDays] = useState<number>(7)
  const [message, setMessage] = useState<string>('')
  const [busy, setBusy] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const [createdLinks, setCreatedLinks] = useState<
    Array<{ approverId: string; phoneMasked: string; token: string }> | null
  >(null)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)

  const handleApproverChange = (index: number, field: keyof ApproverInput, val: unknown) => {
    const str = typeof val === 'string' ? val : (val as { target?: { value?: string } })?.target?.value ?? String(val ?? '')
    setApprovers((prev) => {
      const copy = [...prev]
      copy[index] = { ...copy[index], [field]: str }
      return copy
    })
  }

  const handleExpiryChange = (val: unknown) => {
    const raw = typeof val === 'string' ? val : (val as { target?: { value?: string } })?.target?.value ?? String(val ?? '')
    setExpiresInDays(Number(raw) || 1)
  }

  const handleMessageChange = (val: unknown) => {
    const str = typeof val === 'string' ? val : (val as { target?: { value?: string } })?.target?.value ?? String(val ?? '')
    setMessage(str)
  }

  const handleAddApprover = () => {
    if (approvers.length >= 5) return
    setApprovers((prev) => [...prev, { name: '', phone: '' }])
  }

  const handleRemoveApprover = (index: number) => {
    if (approvers.length <= 1) return
    setApprovers((prev) => prev.filter((_, i) => i !== index))
  }

  const handleCopyLink = async (token: string) => {
    const url = `${window.location.origin}/approve/${token}`
    try {
      await navigator.clipboard.writeText(url)
      setCopiedToken(token)
      setTimeout(() => setCopiedToken(null), 2500)
    } catch {
      // Fallback
    }
  }

  const handleShareLink = async (token: string, approverName: string) => {
    const url = `${window.location.origin}/approve/${token}`
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: t('NLSOpsOutsideApprovalsTitle'),
          text: approverName ? `${approverName}: approval request` : url,
          url,
        })
      } catch {
        // User cancelled or share failed
      }
    } else {
      void handleCopyLink(token)
    }
  }

  const handleSubmit = async () => {
    setError(null)

    // Validation
    if (approvers.length < 1 || approvers.length > 5) {
      setError(t('NLSOpsApprovalApproversLabel'))
      return
    }

    const cleanedPhones: string[] = []
    for (const app of approvers) {
      const name = app.name.trim()
      if (name.length < 1 || name.length > 80) {
        setError(t('NLSOpsApprovalNameRequiredError'))
        return
      }

      const digits = app.phone.replace(/\D/g, '')
      if (digits.length < 7) {
        setError(t('NLSOpsApprovalPhoneInvalidError'))
        return
      }

      if (cleanedPhones.includes(digits)) {
        setError(t('NLSOpsApprovalDistinctPhonesError'))
        return
      }
      cleanedPhones.push(digits)
    }

    if (expiresInDays < 1 || expiresInDays > 30) {
      setError(t('NLSOpsApprovalExpiryDays'))
      return
    }

    if (message.length > 500) {
      setError(t('NLSOpsApprovalMessage'))
      return
    }

    setBusy(true)
    try {
      const opId = crypto.randomUUID()
      const formattedApprovers = approvers.map((a) => ({
        name: a.name.trim(),
        phone: a.phone.startsWith('+') ? a.phone : `+${a.phone}`,
      }))

      if (onSendCustom) {
        const customRes = await onSendCustom({
          workspaceId,
          approvers: formattedApprovers,
          mode,
          expiresInDays,
          message: message.trim() || undefined,
          opId,
        })
        if (customRes.links && customRes.links.length > 0) {
          setCreatedLinks(customRes.links)
        } else {
          onSuccess?.()
          onDismiss()
        }
      } else {
        const result = await createOutsideApproval({
          workspaceId,
          subjectType,
          subjectId,
          approvers: formattedApprovers,
          mode,
          expiresInDays,
          message: message.trim() || undefined,
          opId,
        })

        setCreatedLinks(result.links)
      }
    } catch (err: unknown) {
      setError((err as Error)?.message || String(err))
    } finally {
      setBusy(false)
    }
  }

  const handleCloseDone = () => {
    onSuccess?.()
    onDismiss()
  }

  if (createdLinks) {
    return (
      <AppDialog
        open={open}
        onDismiss={handleCloseDone}
        title={t('NLSOpsApprovalLinksShownOnce')}
        size="md"
        actions={[
          {
            label: t('NLSOpsRequestsVerifiedLabel') || 'Done',
            variant: 'primary',
            onClick: handleCloseDone,
          },
        ]}
      >
        <div>
          <p className="outsideApprovalLinksNotice">
            {t('NLSOpsApprovalLinksShownOnce')}
          </p>
          <div className="outsideApprovalLinksModalList">
            {createdLinks.map((link) => {
              const url = `${typeof window !== 'undefined' ? window.location.origin : ''}/approve/${link.token}`
              const isCopied = copiedToken === link.token
              return (
                <div key={link.token} className="outsideApprovalLinkRow">
                  <div className="outsideApprovalLinkHeader">
                    <span style={{ fontWeight: 600 }}>{link.phoneMasked}</span>
                  </div>
                  <input
                    type="text"
                    readOnly
                    value={url}
                    className="outsideApprovalLinkUrl"
                    aria-label={link.phoneMasked}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleCopyLink(link.token)}
                    >
                      {isCopied ? t('NLSOpsApprovalLinkCopied') : t('NLSOpsApprovalCopyLink')}
                    </Button>
                    {typeof navigator !== 'undefined' && 'share' in navigator ? (
                      <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => handleShareLink(link.token, link.phoneMasked)}
                      >
                        {t('NLSOpsApprovalShareLink')}
                      </Button>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </AppDialog>
    )
  }

  return (
    <AppDialog
      open={open}
      onDismiss={onDismiss}
      title={t('NLSOpsOutsideApprovalsTitle')}
      size="md"
      actions={[
        {
          label: t('NLSOpsRequestsCancel'),
          variant: 'secondary',
          onClick: onDismiss,
          disabled: busy,
        },
        {
          label: t('NLSOpsOutsideApprovalsTitle'),
          variant: 'primary',
          onClick: handleSubmit,
          disabled: busy,
          loading: busy,
        },
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && (
          <div className="outsideApprovalErrorBanner" role="alert">
            {error}
          </div>
        )}

        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 8 }}>
            {t('NLSOpsApprovalApproversLabel')}
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {approvers.map((appr, idx) => (
              <div key={idx} className="outsideApprovalFormApproverRow">
                <div style={{ flex: 1 }}>
                  <TextInput
                    value={appr.name}
                    onChange={(val) => handleApproverChange(idx, 'name', val)}
                    placeholder={t('NLSOpsApprovalApproverName')}
                    aria-label={`${t('NLSOpsApprovalApproverName')} ${idx + 1}`}
                    disabled={busy}
                    maxLength={80}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <PhoneNumberInput
                    value={appr.phone}
                    onChange={(val) => handleApproverChange(idx, 'phone', val)}
                    disabled={busy}
                    localPlaceholder={t('NLSOpsApprovalApproverPhone')}
                  />
                </div>
                {approvers.length > 1 && (
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => handleRemoveApprover(idx)}
                    disabled={busy}
                    aria-label={`${t('NLSOpsApprovalRemoveApprover')} ${idx + 1}`}
                  >
                    {t('NLSOpsApprovalRemoveApprover')}
                  </Button>
                )}
              </div>
            ))}
          </div>

          {approvers.length < 5 && (
            <div style={{ marginTop: 8 }}>
              <Button
                variant="tertiary"
                size="sm"
                onClick={handleAddApprover}
                disabled={busy}
              >
                {t('NLSOpsApprovalAddApprover')}
              </Button>
            </div>
          )}
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 8 }}>
            Mode
          </label>
          <SegmentedControl
            options={[
              { value: 'ALL', label: t('NLSOpsApprovalModeAll') },
              { value: 'ANY', label: t('NLSOpsApprovalModeAny') },
            ]}
            value={mode}
            onChange={(m) => setMode(m as OutsideApprovalMode)}
            ariaLabel="Approval mode"
            disabled={busy}
          />
          <p className="outsideApprovalModeDesc">
            {mode === 'ALL'
              ? t('NLSOpsApprovalModeAllDesc')
              : t('NLSOpsApprovalModeAnyDesc')}
          </p>
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 8 }}>
            {t('NLSOpsApprovalExpiryDays')}
          </label>
          <TextInput
            type="number"
            value={String(expiresInDays)}
            onChange={handleExpiryChange}
            min={1}
            max={30}
            disabled={busy}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 8 }}>
            {t('NLSOpsApprovalMessage')}
          </label>
          <Textarea
            value={message}
            onChange={handleMessageChange}
            placeholder={t('NLSOpsApprovalMessage')}
            maxLength={500}
            disabled={busy}
            rows={3}
          />
        </div>

        <div className="outsideApprovalSmsNotice">
          {t('NLSOpsApprovalSmsNotice')}
        </div>
      </div>
    </AppDialog>
  )
}
